/**
 * Student-answer extraction — ONE JOB PER SUBMISSION.
 *
 * The third granularity in this directory, and the only one that is neither per-exam nor per-main-task: the
 * step already fanned out over submissions with `Promise.all`, and a submission is the natural unit because
 * it writes only its OWN document. That is what lets it carry `submissionLockKey` instead of the task-tree
 * lock, so answer extraction runs ALONGSIDE master-solution and grading-scheme work rather than queueing
 * behind it — the concurrency `pipeline/run.ts` used to get from a promise shape, now expressed as a lock
 * granularity.
 *
 * The fan-out is what this job type IS, so the step was split apart to expose the unit rather than being called
 * with a one-element list: `extractAnswersForSubmission` is the body of that old `Promise.all`. Calling the whole
 * step per job instead meant re-opening and re-closing the exam-wide progress record once per submission.
 *
 * No `desired`: `desired(exam)` only ever sees the exam, and which submissions lack extracted answers is a
 * fact about the SUBMISSION collection. The upload and split sites enqueue these.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Submission } from '../../../submission.js';
import { extractAnswersForSubmission, openAnswerExtractionRun } from '../../pipeline/student-answers-step.js';
import type { FinishedRun, StepCompletion } from '../step.js';
import * as conditions from '../job-conditions.js';
import { submissionLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface AnswerExtractionPayload {
  submissionId: string;
  /**
   * This is a retry of a submission the progress total already counts (a failed extraction being run again),
   * as opposed to newly arrived work.
   *
   * It changes NOTHING about the work: the bookkeeping it used to select — hand back the finished count, clear
   * the error marks — is what `markUnitStarted` does per unit now, so no caller has to know whether a round is
   * new work or a repeat. It stays in the payload purely to keep the two apart in the dedupe key: a retry and
   * a first attempt for one submission are not the same row, and the first attempt's row may still be live.
   */
  retry?: boolean;
  [key: string]: unknown;
}

/**
 * WHAT HAPPENS WHEN THE `answer` STEP FINISHES. Defined here rather than in `steps/` because this type is its ONLY
 * producer — `STEP_JOB_TYPES.answer` is `['answerExtraction']` — so there is nobody to share it with, and the
 * re-grade it decides about is dispatched by this same file's handler a few lines down.
 *
 * NOTHING IS DISPATCHED FROM THE FOLLOW-UP, and that is the one place removing batching changed behaviour rather
 * than machinery. Grading the newly-extracted submissions needs to know WHICH they were, and the batch key was the
 * only record of that — `finishedUnits` on the progress record is cumulative, so it names every submission the exam
 * ever extracted and would re-grade the whole cohort for three new arrivals.
 *
 * So each row dispatches for its OWN submission as it finishes (see the handler), and `dispatchRegradeIfGrading`
 * notes the id on the exam's re-grade ledger and enqueues the grading rows. The round reassembles itself from
 * there: the rows share a `dedupeKey`, so the whole batch is one row, and it holds off until the exam stops
 * changing. The party that knows its own submission id is the one that reports it, and nothing has to mint a key
 * for the set.
 *
 * What is left for the step's completion is the ANNOUNCEMENT: suppress it when a re-grade wave is in flight, because
 * it announces its own result and announcing twice for one upload is what the return value exists to prevent. An open
 * grading BATCH is that signal — per wave, and unable to outlive the rows it belongs to.
 */
const answerStep: StepCompletion = {
  progress: {
    message: 'progress.answersExtracted',
    errorMessage: 'progress.errorExtractingAnswers',
    delta: record => ({ studentAnswer: { reupload: record.reupload === true } }),
  },
  // ASKS THE GRADING WAVE, not the exam. This used to read `examState.pendingRegradeSubmissionIds`, which was an
  // exam-level accumulator: an entry left behind by a request that queued nothing silenced this announcement on
  // every later upload, permanently. An open grading batch is per-wave and cannot outlive its rows.
  followUp: async (_run: FinishedRun) => {
    const { regradeWaveInFlight } = await import('../../pipeline/regrade.js');
    return regradeWaveInFlight(_run.examId);
  },
};

export const answerExtractionJob = defineJob<AnswerExtractionPayload>({
  type: 'answerExtraction',

  /**
   * ONE ROW PER SUBMISSION, and the fan-out is the point. A submission writes only its own document, so this type
   * carries `submissionLockKey` rather than the task-tree lock — which is what lets four hundred of them spread
   * across the fleet and run alongside master-solution work instead of behind it. It is also what makes a re-upload
   * cheap: the already-live rows dedupe away by submission id and only the new arrivals get work.
   *
   * A ROUND IS A RUN. The submissions confirmed together are extracted together and the run closes when the last of
   * them lands; a later re-upload confirms its own round and opens its own.
   *
   * `retry` marks a round that redoes submissions the total already counts. It changes nothing about the work — the
   * bookkeeping is per unit — but keeps a retry and a first attempt apart in the dedupe key, because the first
   * attempt's row may still be live.
   *
   * `joinTwin`, because the retry tool waits: clicking retry twice must report on the first click's job rather than
   * on "nothing to do".
   */
  /**
   * The step opens with its own status and total — the submissions being extracted are counted here.
   *
   * `reupload` is the one thing the record cannot derive for this type, which is why it is a function of the scope.
   * The derivation ("this step already reached COMPLETED or ERROR once") is exactly right for a new ROUND and exactly
   * wrong for a RETRY: after any first round the record reads COMPLETED, so a teacher clicking retry on a failed
   * submission was told their answers had been RE-UPLOADED. A retry is a second attempt at the same delivery, so it
   * declares `false`; a round says nothing and keeps the derivation.
   */
  onStepStart: {
    reupload: scope => ((scope as { retry?: boolean }).retry ? false : undefined),
    open: examId => openAnswerExtractionRun(examId),
  },

  batch: {
    units: (_examId, scope) => {
      const { submissionIds = [], retry } = scope as { submissionIds?: string[]; retry?: boolean };
      return submissionIds.map(submissionId => ({ submissionId, ...(retry ? { retry: true } : {}) }));
    },
    joinTwin: true,
    /**
     * Sized to ONE attempt, not to this type's three. `answerExtraction` allows 10 minutes per attempt, and a
     * submission that has already burned an attempt plus its backoff is no longer something to hold a chat turn open
     * for — the retry tool is the only caller that waits.
     */
    waitMs: 12 * 60_000,
  },

  /**
   * Closes the `answer` step when this exam has no `answerExtraction` row left. Nothing is supplied at enqueue
   * time — a round is not an object, it is the absence of live rows — so a re-upload needs no key of its own to
   * avoid being mistaken for the round before it.
   *
   * The `anySetupStepOngoing` guard that used to sit here is gone: this step's completion says nothing about a
   * master solution still generating, and the coalescing across steps is `updateSetupStep`'s own gate — which
   * still reports the split confirmation, the human gate the queue will never know about.
   */
  onStepComplete: answerStep,

  // Writes only this submission's document. The whole reason this type is per-submission.
  lockKey: (_examId, payload) => submissionLockKey(payload.submissionId),

  dedupeKey: (_examId, payload) => `answerExtraction:${payload.submissionId}${payload.retry ? ':retry' : ''}`,

  ready: exam => {
    // The extraction matches answers against the confirmed task tree, so it needs the teacher's sign-off. The
    // pipeline enforced this by only calling the step after the confirmation; a row can be enqueued at any
    // time, so the gate has to be stated here.
    if (!conditions.structureConfirmed(exam)) {
      return { verdict: 'wait', because: 'answerExtraction:structure-unconfirmed' };
    }
    // The SPLIT gate is deliberately not here. `splitConfirmed` is a per-SUBMISSION flag and `ready()` only
    // ever sees the exam, so the exam-level `splitConfirmationState` is the wrong document to ask: it is
    // flipped by the latest confirmation, and after a re-upload it would hold back jobs for submissions that
    // were confirmed rounds ago. `confirmSubmissionSplitMutation` enqueues these jobs for exactly the
    // submissions it has just confirmed, and the handler re-checks the flag below.
    return 'run';
  },

  /** One submission's pages through OCR. Far cheaper than an exam-wide step, and priced accordingly. */
  maxRuntimeMs: 10 * 60_000,
  maxAttempts: 3,

  handler: async (examId, payload, context) => {
    // Re-checked after the claim, and here rather than in `ready()` because it is a fact about the
    // submission. Extracting against an unconfirmed split reads pages that may still be reassigned, which
    // silently attributes one student's answers to another — worth a read to rule out.
    const submissionCollection = await getCollection(Submission);
    const submission = await submissionCollection.findOne(
      { _id: payload.submissionId },
      { projection: { splitConfirmed: 1 } },
    );
    if (!submission) return { skipped: true, note: 'submission no longer exists' };
    if ((submission as any).splitConfirmed !== true) {
      // Enqueue happens AT confirmation, so reaching this means the assignment was reopened afterwards. The
      // next confirmation enqueues a fresh job, so there is nothing for this row to wait for.
      return { skipped: true, note: 'split no longer confirmed for this submission' };
    }

    // The UNIT, not the step. The deleted `runStudentAnswersStep` opened a run and closed it, and calling that
    // per job meant every submission re-closed the exam-wide progress record: after the first of ten landed,
    // the record read COMPLETED at 1/10 and `anySetupStepOngoing` believed it. This only does one submission's
    // work; `onStepComplete` above closes the step once no submission of it is left.
    const extracted = await extractAnswersForSubmission(examId, payload.submissionId);

    // Nothing is announced from here. The announcement follows this row reaching a terminal status, which is
    // also why the throw below no longer suppresses it: `onStepComplete` fires on `failed` too, so a round
    // whose last submission failed still closes instead of leaving the bar spinning.
    //
    // A failed submission is recorded in `examState.answerExtractionProgress.errorSubmissions` by the unit
    // itself, for the retry tool to offer. Failing the ROW as well is what gets the attempt retried first.
    if (!extracted) throw new Error(`Answer extraction failed for submission ${payload.submissionId}`);

    // GRADING THIS SUBMISSION IS THIS ROW'S BUSINESS, not the step's — and that relocation is what let
    // the batch go. Grading newly-extracted work has to know WHICH submissions were extracted, and a batch key
    // was the only record of that: `finishedUnits` on the progress record is cumulative, so it names every
    // submission the exam ever extracted and would re-grade the whole cohort for three new arrivals.
    //
    // A row knows its own submission id, so it says so, and `dispatchRegradeIfGrading` records it on the ledger
    // and enqueues grading. Ten submissions finishing over two minutes produce ONE grading row per task, because
    // the rows dedupe on their scope and wait for the exam to settle before running — see `GradingPayload.settle`.
    //
    // Gated on `hasGradingStarted`, read fresh: on an exam that has never been graded there is nothing to
    // re-grade, and the first grading is dispatched by whoever starts it.
    if ((context.exam as any)?.examState?.hasGradingStarted) {
      const { dispatchRegradeIfGrading } = await import('../../pipeline/regrade.js');
      await dispatchRegradeIfGrading({
        examId,
        causes: ['new_submissions'],
        submissionIds: [payload.submissionId],
        // This submission is being graded for the first time, not re-graded because something changed —
        // batch a solo language-use rule into the same call, same as the exam's very first grading run.
        includeSoloLanguageUse: true,
      }).catch(err =>
        // Non-fatal: the answers ARE extracted and the row must report that truthfully. A lost re-grade costs
        // the teacher a manual trigger, not the extraction.
        logger.error(
          { err, examId, submissionId: payload.submissionId },
          'Could not queue a re-grade for this submission',
        ),
      );
    }

    return { note: 'answers extracted' };
  },
});
